import { Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { TariffSelectionCardData, TextNodeType } from '@mwe/models';
import { ProductCategoryEnum, ServiceStateEnum } from '@mwe/constants';
import { HeadlineService } from '../../headline/headline.service';
import { TariffSelectionCardLogic } from '../services/tariff-selection-card.logic';
import { BasicCardComponent } from '../../cards/basic-card/basic-card.component';
import { NgClass } from '@angular/common';
import { HeadlineComponent } from '../../headline/headline.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { CorrelatedCompanyReferencePipe } from '@mwe/ui/pipes';
import { formatCurrencyValue, isProductCategory } from '@mwe/utils';

@Component({
  selector: 'mwe-tariff-card-additional-options-selection',
  templateUrl: './tariff-card-additional-options-selection.component.html',
  providers: [HeadlineService],
  imports: [BasicCardComponent, NgClass, HeadlineComponent, TranslatePipe, TranslateDirective, CorrelatedCompanyReferencePipe],
})
export class TariffCardAdditionalOptionsSelectionComponent implements OnInit {
  // tariff comes from signal() do not manipulate it!!!! use TariffSelectionStore!!!!!
  tariff = input.required<TariffSelectionCardData>();
  iconClass = input<string>('fa-tv');
  isReadOnly = input<boolean>(false);
  headlineReference = input<TextNodeType>('h4');
  tariffSheetUrls = input<Record<string, string>>();

  readonly headlineService = inject(HeadlineService);
  private readonly cardLogic = inject(TariffSelectionCardLogic);

  selectedTvOption = signal<string>('');
  isSelected = signal<boolean>(false);

  readonly title = computed<string>(() => 'tariff.additionalOptions.iptv.label');
  readonly isMobile = computed<boolean>(() => isProductCategory(this.tariff()?.category, ProductCategoryEnum['MOBILES INTERNET']));
  readonly description = computed<string>(() => 'tariff.additionalOptions.iptv.cardText');
  readonly helpLabel = computed<string>(() => 'tariff.additionalOptions.iptv.helpLabel');
  readonly productDetailsTitle = computed<string>(() => 'tariff.additionalOptions.iptv.productDetails.title');
  readonly tvProductLinkLabel = computed<string>(() => 'tariff.additionalOptions.iptv.productDetails.link.label');
  readonly tariffLinkLabel = computed<string>(() => 'tariff.selectionCard.mvnoTariffPdfLink');
  readonly basePriceLabel = computed<string>(() => 'tariff.additionalOptions.iptv.basePriceLabel');
  readonly basePriceUnit = computed<string>(() => 'tariff.additionalOptions.iptv.basePriceUnit');
  readonly productDetailsText = computed(() =>
    this.selectedTvOption() === 'WEBIPTV26STREAM'
      ? 'tariff.additionalOptions.iptv.productDetails.tvStreaming'
      : 'tariff.additionalOptions.iptv.productDetails.tvPremium',
  );

  readonly allOptions = computed(() =>
    this.tariff()?.tariffOptionsSap?.map(tariffOption => {
      return {
        ...tariffOption,
        ...this.tariff()?.priceIndication?.internet?.tvKosten?.find(
          priceIndication =>
            priceIndication?.produktKey === tariffOption?.isuTariffKey ||
            priceIndication?.produktKey === (tariffOption as any)?.ISUTarifKey ||
            priceIndication?.produktKey === tariffOption.option,
        ),
      };
    }),
  );
  readonly dropdownOptions = computed(() =>
    this.allOptions()?.filter(option => !!option?.isuTariffKey || !!option?.option || !!(option as any)?.ISUTarifKey),
  );
  readonly basePrice = computed(() =>
    formatCurrencyValue(
      this.dropdownOptions()?.find(
        dropdownOption =>
          dropdownOption?.produktKey === this.selectedTvOption() || (dropdownOption as any)?.ISUTarifKey === this.selectedTvOption(),
      )?.preis,
    ),
  );
  readonly isCheckBoxDisabled = computed(() => this.tariff()?.state !== ServiceStateEnum.SUCCESS || this.isReadOnly());

  ngOnInit(): void {
    this.headlineService.setReferenceHeadline(this.headlineReference());
    this.initializeFromStore();
  }

  private initializeFromStore(): void {
    const selectedOption = this.tariff().userSelection?.find(u => u.art === 'ZUSATZOPTIONEN');
    if (selectedOption) {
      this.selectedTvOption.set(selectedOption.option);
      this.isSelected.set(true);
    } else {
      this.selectedTvOption.set(this.dropdownOptions()[0]?.produktKey);
      this.isSelected.set(false);
    }
  }

  onCheckboxChange(): void {
    this.isSelected.update(old => !old);

    if (this.isSelected()) {
      this.selectedTvOption.set(this.dropdownOptions()[0]?.produktKey);
      this.updateStore();
    } else {
      this.removeFromStore();
    }
  }

  selectOption(target: EventTarget): void {
    this.selectedTvOption.set((target as HTMLSelectElement)?.value);
    this.updateStore();
  }

  private updateStore(): void {
    this.cardLogic.handleTariffOptionChange(this.tariff(), {
      option: this.selectedTvOption(),
      art: 'ZUSATZOPTIONEN',
    });
  }

  private removeFromStore(): void {
    this.cardLogic.updateCardData({ ...this.tariff(), userSelection: [] });
  }
}
